<template>
  <v-card flat class="pa-2">
    <v-row dense>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="filtros.idArea"
          :items="areas"
          item-text="nombre"
          item-value="idArea"
          label="Area (Opcional)"
          clearable
          :loading="loadingCatalogos"
          :disabled="loadingCatalogos || loading"
          @change="onAreaChange"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="filtros.idMaquinaIns"
          :items="maquinasFiltradas"
          :item-text="nombreMaquina"
          item-value="idMaquinaIns"
          label="Maquina"
          clearable
          :loading="loadingCatalogos"
          :disabled="loadingCatalogos || loading"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          v-model="filtros.fecha"
          type="date"
          label="Fecha"
          :disabled="loading"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-autocomplete
          v-model="filtros.idTurno"
          :items="turnosConTodos"
          item-text="nombre"
          item-value="idTurno"
          label="Turno"
          :loading="loadingCatalogos"
          :disabled="loadingCatalogos || loading"
        />
      </v-col>
      <v-col cols="12" md="2" class="d-flex align-center">
        <v-btn color="primary" block :loading="loading" @click="consultar">
          Consultar flujo
        </v-btn>
      </v-col>
    </v-row>

    <v-alert
      v-if="error"
      dense
      text
      type="error"
      class="mb-3"
    >
      {{ error }}
    </v-alert>

    <v-row dense class="mb-2" v-if="segmentosDia.length">
      <v-col cols="12" md="3">
        <v-chip color="blue lighten-5" text-color="blue darken-3" label>
          Paros detectados: {{ totalParosDia }}
        </v-chip>
      </v-col>
      <v-col cols="12" md="3">
        <v-chip color="orange lighten-5" text-color="orange darken-3" label>
          Paro no programado: {{ toTime(totalParoNoProgramadoSegDia) }}
        </v-chip>
      </v-col>
      <v-col cols="12" md="3">
        <v-chip color="amber lighten-5" text-color="amber darken-4" label>
          Paro programado: {{ toTime(totalParoProgramadoSegDia) }}
        </v-chip>
      </v-col>
      <v-col cols="12" md="3">
        <v-chip color="deep-purple lighten-5" text-color="deep-purple darken-3" label>
          Caida de piezas: {{ totalCaidaPiezasDia }} pzs ({{ marcasCaida.length }} eventos)
        </v-chip>
      </v-col>
    </v-row>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />

    <v-alert v-if="!loading && consultado && !segmentosDia.length" dense text color="blue-grey">
      No hay datos de paros para la seleccion actual.
    </v-alert>

    <v-card v-if="segmentosDia.length" outlined class="mb-3 pa-3">
      <div class="subtitle-2 font-weight-bold mb-2">
        Flujo del dia (24h)
      </div>
      <div
        class="dia-bar-wrap"
        @mouseleave="hoveredClusterIdx = null"
      >
        <div class="dia-bar">
          <div
            v-for="(seg, i) in segmentosDia"
            :key="`seg-${i}`"
            :class="segmentClass(seg.tipo)"
            :style="{ width: `${porcentaje(seg.segundos, 24 * 3600)}%` }"
            :title="segmentTooltip(seg)"
          />
        </div>
        <div
          v-for="(m, i) in markerClusters"
          :key="`mark-all-${i}`"
          class="cluster-hotspot"
          :class="{ 'is-active': hoveredClusterIdx === i }"
          :style="clusterStyle(m)"
          @mouseenter="hoveredClusterIdx = i"
          @focusin="hoveredClusterIdx = i"
        >
          <div
            class="overlay-mark"
            :class="`mark-${m.clusterType}`"
            :title="m.tooltip"
          >
            <span class="mark-label">{{ m.label }}</span>
          </div>
          <div class="cluster-fan">
            <button
              v-for="(eventMark, markIdx) in m.marks"
              :key="`mark-event-${i}-${markIdx}`"
              type="button"
              class="event-needle"
              :class="`event-${eventMark.tipo}`"
              :style="eventNeedleStyle(markIdx, m.marks.length)"
              :title="eventMark.tooltip"
              @mouseenter="hoveredClusterIdx = i"
              @focus="hoveredClusterIdx = i"
            />
          </div>
        </div>
        <div
          v-if="activeCluster"
          class="cluster-inspector"
          :style="inspectorStyle(activeCluster)"
        >
          <div class="inspector-title">
            {{ activeCluster.timeTxt }}
          </div>
          <div
            v-for="(eventMark, markIdx) in activeCluster.marks"
            :key="`detail-${markIdx}`"
            class="inspector-row"
          >
            <span class="inspector-dot" :class="`dot-${eventMark.tipo}`" />
            <span>{{ eventMark.detail }}</span>
          </div>
        </div>
      </div>
      <div class="d-flex justify-space-between caption mt-2 grey--text text--darken-1">
        <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span>
      </div>
      <div class="d-flex flex-wrap mt-2">
        <v-chip x-small class="mr-2 mb-1" color="teal lighten-4">Tiempo activo</v-chip>
        <v-chip x-small class="mr-2 mb-1" color="red lighten-3">Paro no programado</v-chip>
        <v-chip x-small class="mr-2 mb-1" color="amber lighten-3">Paro programado</v-chip>
        <v-chip x-small class="mr-2 mb-1" color="deep-purple lighten-3">Caida de piezas</v-chip>
        <v-chip x-small class="mr-2 mb-1" color="orange lighten-3">Micro paro (&lt; 2.59 min)</v-chip>
        <v-chip x-small class="mr-2 mb-1" color="blue-grey lighten-4">Mixto</v-chip>
      </div>

    </v-card>

    <div v-if="parosDia.length" class="subtitle-2 font-weight-bold mb-2">
      Paros identificados
    </div>
    <v-data-table
      v-if="parosDia.length"
      :headers="headersParos"
      :items="parosDia"
      dense
      class="elevation-1"
      :items-per-page="20"
    />
  </v-card>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FlujoParosTurno',
  data() {
    return {
      MICRO_PARO_MINUTES: 2.59,
      TURNO_TODOS: '__ALL__',
      loadingCatalogos: false,
      loading: false,
      consultado: false,
      error: null,
      areas: [],
      maquinas: [],
      turnos: [],
      horasDia: [],
      segmentosDia: [],
      eventosDia: [],
      parosDia: [],
      marcasCaida: [],
      marcasMicroParo: [],
      marcasParo: [],
      hoveredClusterIdx: null,
      headersParos: [
        { text: 'Inicio', value: 'inicioTxt', sortable: true },
        { text: 'Fin', value: 'finTxt', sortable: true },
        { text: 'Duracion', value: 'duracionSeg', sortable: true },
        { text: 'Tipo', value: 'tipoTxt', sortable: true },
        { text: 'Clasificacion', value: 'clasificacion', sortable: true },
        { text: 'Detalle', value: 'detalle', sortable: false },
      ],
      filtros: {
        idArea: null,
        idMaquinaIns: null,
        fecha: null,
        idTurno: '__ALL__',
      },
    };
  },
  computed: {
    turnosConTodos() {
      return [{ idTurno: this.TURNO_TODOS, nombre: 'Todos los turnos' }, ...this.turnos];
    },
    maquinasFiltradas() {
      if (!this.filtros.idArea) return this.maquinas;
      return this.maquinas.filter(m => Number(m.idArea) === Number(this.filtros.idArea));
    },
    totalParosDia() {
      return this.eventosDia.filter(e => e.tipo === 'noProg' || e.tipo === 'prog').length;
    },
    totalParoNoProgramadoSegDia() {
      return this.segmentosDia.filter(s => s.tipo === 'noProg').reduce((a, s) => a + s.segundos, 0);
    },
    totalParoProgramadoSegDia() {
      return this.segmentosDia.filter(s => s.tipo === 'prog').reduce((a, s) => a + s.segundos, 0);
    },
    totalCaidaPiezasDia() {
      return this.eventosDia
        .filter(e => e.tipo === 'caida')
        .reduce((a, e) => a + Number(e.piezasCaidas || 0), 0);
    },
    markerClusters() {
      const raw = [
        ...this.marcasCaida.map(m => ({ ...m, tipo: 'caida' })),
        ...this.marcasMicroParo.map(m => ({ ...m, tipo: 'micro' })),
        ...this.marcasParo.map(m => ({ ...m, tipo: 'paro' })),
      ].sort((a, b) => a.leftPct - b.leftPct);

      if (!raw.length) return [];

      const thresholdPct = 0.45; // agrupa eventos muy cercanos en tiempo
      const clusters = [];

      raw.forEach((m) => {
        const last = clusters[clusters.length - 1];
        if (!last || Math.abs(m.leftPct - last.leftPctAvg) > thresholdPct) {
          clusters.push({
            marks: [m],
            leftPctAvg: m.leftPct,
          });
          return;
        }
        last.marks.push(m);
        last.leftPctAvg = last.marks.reduce((s, x) => s + x.leftPct, 0) / last.marks.length;
      });

      return clusters.map((c) => {
        const types = Array.from(new Set(c.marks.map(x => x.tipo)));
        const clusterType = types.length === 1 ? types[0] : 'mix';
        const label = c.marks.length > 1 ? String(c.marks.length) : (clusterType === 'caida' ? 'C' : clusterType === 'micro' ? 'M' : clusterType === 'paro' ? 'P' : 'X');
        const lines = c.marks.slice(0, 5).map(x => x.tooltip);
        if (c.marks.length > 5) lines.push(`+${c.marks.length - 5} eventos`);

        return {
          leftPct: c.leftPctAvg,
          topPct: 4,
          clusterType,
          label,
          marks: c.marks,
          timeTxt: this.hhmm(c.marks[0]?.start || this.parseDateTime(this.filtros.fecha, '00:00')),
          tooltip: lines.join(' | '),
        };
      });
    },
    activeCluster() {
      if (this.hoveredClusterIdx == null) return null;
      return this.markerClusters[this.hoveredClusterIdx] || null;
    },
    microParoSegundos() {
      return this.MICRO_PARO_MINUTES * 60;
    },
  },
  methods: {
    nombreMaquina(m) {
      const nombre = m?.nombre || 'Sin nombre';
      const marca = m?.marca || '';
      const modelo = m?.modelo || '';
      return `[${nombre}] - ${marca} ${modelo}`.trim();
    },
    toTime(segundos) {
      const s = Math.max(0, Number(segundos) || 0);
      const hh = Math.floor(s / 3600);
      const mm = Math.floor((s % 3600) / 60);
      return `${hh} h ${mm} min`;
    },
    fechaHoyMx() {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Mexico_City',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date());
    },
    minutosHoyMx() {
      const [hh, mm] = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'America/Mexico_City',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date()).split(':').map(Number);
      return hh * 60 + mm;
    },
    timeToMin(hhmm) {
      const [h, m] = String(hhmm || '00:00').split(':').map(v => Number(v) || 0);
      return h * 60 + m;
    },
    segmentoTipoTxt(tipo) {
      if (tipo === 'noProg') return 'Paro no programado';
      if (tipo === 'prog') return 'Paro programado';
      if (tipo === 'caida') return 'Caida de piezas';
      return 'Activo';
    },
    segmentClass(tipo) {
      if (tipo === 'noProg') return 'seg-no-prog';
      if (tipo === 'prog') return 'seg-prog';
      return 'seg-activo';
    },
    porcentaje(valor, total) {
      const v = Number(valor || 0);
      const t = Number(total || 0);
      if (!t) return 0;
      return Math.max(0, Math.min(100, (v * 100) / t));
    },
    clusterStyle(m) {
      return {
        left: `${m.leftPct}%`,
        top: `${m.topPct}%`,
      };
    },
    eventNeedleStyle(idx, total) {
      if (total <= 1) return { '--fan-offset': '0px' };
      const spread = Math.min(24, 8 + total * 2.6);
      const step = total > 1 ? spread / (total - 1) : 0;
      const offset = -spread / 2 + idx * step;
      return { '--fan-offset': `${offset}px` };
    },
    inspectorStyle(cluster) {
      const left = Math.max(10, Math.min(90, Number(cluster.leftPct || 0)));
      return { left: `${left}%` };
    },
    segmentTooltip(seg) {
      return `${this.hhmm(seg.inicio)} - ${this.hhmm(seg.fin)} | ${this.segmentoTipoTxt(seg.tipo)} | ${this.toTime(seg.segundos)}`;
    },
    secondsToHm(sec) {
      const s = Math.max(0, Math.floor(Number(sec) || 0));
      const mm = Math.floor(s / 60);
      const ss = s % 60;
      return `${mm}m ${String(ss).padStart(2, '0')}s`;
    },
    hhmm(dt) {
      const d = dt instanceof Date ? dt : new Date(dt);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    },
    parseDateTime(fecha, hhmm) {
      const f = (fecha || this.filtros.fecha || '').slice(0, 10);
      const h = (hhmm || '00:00').slice(0, 5);
      return new Date(`${f}T${h}:00`);
    },
    clipInterval(start, end, dayStart, dayEnd) {
      const s = start > dayStart ? start : dayStart;
      const e = end < dayEnd ? end : dayEnd;
      return e > s ? { start: s, end: e } : null;
    },
    normalizeHoras(data) {
      return Array.isArray(data?.horas) ? data.horas : (Array.isArray(data) ? data : []);
    },
    getNum(obj, keys) {
      for (const k of keys) {
        if (obj && obj[k] != null) return Number(obj[k]) || 0;
      }
      return 0;
    },
    piezasEsperadasHora(h) {
      const cavs = Array.isArray(h?.cavidades) ? h.cavidades : [];
      if (cavs.length) {
        return cavs.reduce((acc, c) => acc + this.getNum(c, ['piezasTeoricasHora', 'PiezasTeoricasHora']), 0);
      }
      return this.getNum(h, ['piezasTeoricasHora', 'PiezasTeoricasHora', 'teoricas', 'Teoricas']);
    },
    piezasOkHora(h) {
      const cavs = Array.isArray(h?.cavidades) ? h.cavidades : [];
      if (cavs.length) {
        return cavs.reduce((acc, c) => acc + this.getNum(c, ['piezasOkHora', 'PiezasOkHora']), 0);
      }
      return this.getNum(h, ['piezasOkHora', 'PiezasOkHora', 'ok', 'Ok']);
    },
    piezasNoOkHora(h) {
      const cavs = Array.isArray(h?.cavidades) ? h.cavidades : [];
      if (cavs.length) {
        return cavs.reduce((acc, c) => acc + this.getNum(c, ['piezasNoOkHora', 'PiezasNoOkHora']), 0);
      }
      return this.getNum(h, ['piezasNoOkHora', 'PiezasNoOkHora', 'noOk', 'NoOk']);
    },
    buildDayData() {
      const dayStart = this.parseDateTime(this.filtros.fecha, '00:00');
      const dayEnd = new Date(dayStart.getTime() + 24 * 3600 * 1000);

      const parosProg = [];
      const parosNoProg = [];
      const caidas = [];
      const eventos = [];
      const paros = [];

      for (const h of this.horasDia) {
        const fecha = h.fecha || h.Fecha || this.filtros.fecha;
        const hStart = this.parseDateTime(fecha, h.desdeHora || h.horaInicio || '00:00');
        let hEnd = this.parseDateTime(fecha, h.hastaHora || h.horaFin || '00:00');
        if (hEnd <= hStart) hEnd = new Date(hEnd.getTime() + 24 * 3600 * 1000);
        const hClip = this.clipInterval(hStart, hEnd, dayStart, dayEnd);
        if (!hClip) continue;

        const esperadas = this.piezasEsperadasHora(h);
        const ok = this.piezasOkHora(h);
        const noOk = this.piezasNoOkHora(h);
        const deficit = Math.max(0, esperadas - (ok + noOk));
        if (deficit > 0) {
          caidas.push({ start: hClip.start, end: hClip.end, piezas: deficit });
          eventos.push({
            inicio: hClip.start,
            fin: hClip.end,
            inicioTxt: this.hhmm(hClip.start),
            finTxt: this.hhmm(hClip.end),
            tipo: 'caida',
            tipoTxt: this.segmentoTipoTxt('caida'),
            detalle: `Caida de ${deficit} pzs (Esperadas ${esperadas}, Reales ${ok + noOk})`,
            piezasCaidas: deficit,
          });
        }

        const pushParos = (arr, tipo, out) => {
          arr.forEach(p => {
            if (!p?.inicioBloque || !p?.finBloque) return;
            const pStart = new Date(p.inicioBloque);
            const pEnd = new Date(p.finBloque);
            if (!(pEnd > pStart)) return;
            const clip = this.clipInterval(pStart, pEnd, dayStart, dayEnd);
            if (!clip) return;
            out.push({ start: clip.start, end: clip.end, causa: p.causa || '', subCausa: p.subCausa || '' });
            const causa = [p.causa, p.subCausa].filter(Boolean).join(' - ') || this.segmentoTipoTxt(tipo);
            eventos.push({
              inicio: clip.start,
              fin: clip.end,
              inicioTxt: this.hhmm(clip.start),
              finTxt: this.hhmm(clip.end),
              tipo,
              tipoTxt: this.segmentoTipoTxt(tipo),
              detalle: causa,
              piezasCaidas: 0,
            });
            const durSeg = (clip.end - clip.start) / 1000;
            paros.push({
              inicio: clip.start,
              fin: clip.end,
              inicioTxt: this.hhmm(clip.start),
              finTxt: this.hhmm(clip.end),
              duracionSeg: durSeg,
              duracionTxt: this.secondsToHm(durSeg),
              tipo,
              tipoTxt: this.segmentoTipoTxt(tipo),
              clasificacion: durSeg < this.microParoSegundos ? 'Micro paro' : 'Paro',
              detalle: causa,
            });
          });
        };

        pushParos(Array.isArray(h.parosProgramados) ? h.parosProgramados : [], 'prog', parosProg);
        pushParos(Array.isArray(h.parosNoProgramados) ? h.parosNoProgramados : [], 'noProg', parosNoProg);
      }

      const points = new Set([dayStart.getTime(), dayEnd.getTime()]);
      [...parosProg, ...parosNoProg].forEach(iv => {
        points.add(iv.start.getTime());
        points.add(iv.end.getTime());
      });
      const sorted = Array.from(points).sort((a, b) => a - b);
      const segments = [];
      for (let i = 0; i < sorted.length - 1; i++) {
        const s = new Date(sorted[i]);
        const e = new Date(sorted[i + 1]);
        if (!(e > s)) continue;
        const mid = new Date((s.getTime() + e.getTime()) / 2);

        const inNoProg = parosNoProg.some(iv => mid >= iv.start && mid < iv.end);
        const inProg = parosProg.some(iv => mid >= iv.start && mid < iv.end);
        let tipo = 'activo';
        if (inNoProg) tipo = 'noProg';
        else if (inProg) tipo = 'prog';

        const last = segments[segments.length - 1];
        const segSec = (e - s) / 1000;
        if (last && last.tipo === tipo && last.fin.getTime() === s.getTime()) {
          last.fin = e;
          last.segundos += segSec;
        } else {
          segments.push({ tipo, inicio: s, fin: e, segundos: segSec });
        }
      }

      this.segmentosDia = segments;
      this.eventosDia = eventos
        .sort((a, b) => a.inicio - b.inicio)
        .map(e => ({ ...e, inicioTs: e.inicio.getTime() }));
      this.parosDia = paros
        .sort((a, b) => a.inicio - b.inicio)
        .map(p => ({ ...p, inicioTs: p.inicio.getTime() }));
      this.marcasCaida = caidas.map(c => {
        const secFromStart = (c.start - dayStart) / 1000;
        return {
          start: c.start,
          end: c.end,
          leftPct: this.porcentaje(secFromStart, 24 * 3600),
          detail: `Caida de piezas | ${Math.round(c.piezas)} pzs`,
          tooltip: `${this.hhmm(c.start)} - ${this.hhmm(c.end)} | Caida de piezas (${Math.round(c.piezas)} pzs)`,
        };
      });
      this.marcasMicroParo = paros
        .filter(p => Number(p.duracionSeg || 0) < this.microParoSegundos)
        .map(p => {
          const secFromStart = (p.inicio - dayStart) / 1000;
          return {
            start: p.inicio,
            end: p.fin,
            leftPct: this.porcentaje(secFromStart, 24 * 3600),
            detail: `Micro paro | ${p.duracionTxt} | ${p.detalle}`,
            tooltip: `${p.inicioTxt} - ${p.finTxt} | Micro paro | ${p.duracionTxt} | ${p.detalle}`,
          };
        });
      this.marcasParo = paros
        .filter(p => Number(p.duracionSeg || 0) >= this.microParoSegundos)
        .map(p => {
          const secFromStart = (p.inicio - dayStart) / 1000;
          return {
            start: p.inicio,
            end: p.fin,
            leftPct: this.porcentaje(secFromStart, 24 * 3600),
            detail: `Paro | ${p.duracionTxt} | ${p.detalle}`,
            tooltip: `${p.inicioTxt} - ${p.finTxt} | Paro | ${p.duracionTxt} | ${p.detalle}`,
          };
        });
    },
    onAreaChange() {
      if (!this.filtros.idMaquinaIns) return;
      const existe = this.maquinasFiltradas.some(m => Number(m.idMaquinaIns) === Number(this.filtros.idMaquinaIns));
      if (!existe) this.filtros.idMaquinaIns = null;
    },
    async initialize() {
      this.loadingCatalogos = true;
      this.error = null;
      try {
        const [areasRes, maquinasRes, turnosRes] = await Promise.all([
          axios.get('/Areas'),
          axios.get('/MaquinaInstancias'),
          axios.get('/Turnos'),
        ]);

        this.areas = Array.isArray(areasRes.data) ? areasRes.data : [];
        this.maquinas = Array.isArray(maquinasRes.data) ? maquinasRes.data : [];
        this.turnos = Array.isArray(turnosRes.data) ? turnosRes.data : [];

        this.filtros.fecha = this.fechaHoyMx();
        this.filtros.idTurno = this.TURNO_TODOS;
        this.filtros.idMaquinaIns = this.filtros.idMaquinaIns || (this.maquinas[0] && this.maquinas[0].idMaquinaIns) || null;

        if (this.filtros.idMaquinaIns) {
          await this.consultar();
        }
      } catch (e) {
        this.error = 'No se pudieron cargar catalogos para flujo de turno.';
      } finally {
        this.loadingCatalogos = false;
      }
    },
    async consultar() {
      this.error = null;
      this.consultado = true;
      this.horasDia = [];
      this.segmentosDia = [];
      this.eventosDia = [];
      this.parosDia = [];
      this.marcasCaida = [];
      this.marcasMicroParo = [];
      this.marcasParo = [];
      this.hoveredClusterIdx = null;

      if (!this.filtros.idMaquinaIns) {
        this.error = 'Selecciona una maquina para consultar el flujo.';
        return;
      }
      if (!this.filtros.idTurno) {
        this.error = 'Selecciona un turno para consultar el flujo.';
        return;
      }

      this.loading = true;
      try {
        const turnosConsulta = this.filtros.idTurno === this.TURNO_TODOS
          ? this.turnos
          : this.turnos.filter(t => Number(t.idTurno) === Number(this.filtros.idTurno));

        const reqs = turnosConsulta.map(turno => axios.get('/OEECalculos/HoraPorHora', {
          params: {
            idMaquinaIns: this.filtros.idMaquinaIns,
            fechaRev: this.filtros.fecha,
            idTurno: turno.idTurno,
            sinDisponibilidad: true,
            desConParosNP: false,
          },
        }));

        const results = await Promise.allSettled(reqs);
        results.forEach((res, idx) => {
          if (res.status !== 'fulfilled') return;
          const data = res.value?.data;
          const horas = this.normalizeHoras(data);
          this.horasDia.push(...horas);
        });
        this.buildDayData();
      } catch (e) {
        this.horasDia = [];
        this.segmentosDia = [];
        this.eventosDia = [];
        this.parosDia = [];
        this.marcasCaida = [];
        this.marcasMicroParo = [];
        this.marcasParo = [];
        this.hoveredClusterIdx = null;
        this.error = 'Error consultando flujo de paros del turno.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.dia-bar {
  width: 100%;
  height: 30px;
  border-radius: 999px;
  overflow: hidden;
  display: flex;
  background: #eceff1;
}

.dia-bar > div {
  position: relative;
  z-index: 1;
  transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;
  transform-origin: center center;
  cursor: pointer;
}

.dia-bar > div:hover {
  transform: scaleY(1.35);
  z-index: 3;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
  filter: saturate(1.1);
}
.dia-bar-wrap {
  position: relative;
  padding-bottom: 96px;
}

.seg-activo {
  background: #26a69a;
  min-width: 1px;
}

.seg-no-prog {
  background: #ef5350;
  min-width: 2px;
}

.seg-prog {
  background: #ffb300;
  min-width: 2px;
}

.cluster-hotspot {
  position: absolute;
  width: 28px;
  margin-left: -14px;
  height: 58px;
  z-index: 5;
}

.overlay-mark {
  position: absolute;
  left: 50%;
  top: 6px;
  transform: translateX(-50%);
  width: 20px;
  height: 30px;
  background: transparent;
  cursor: pointer;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-mark::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  transform: translate(-50%, -50%) scaleY(1);
  transform-origin: center center;
  transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;
}

.overlay-mark:hover::before {
  transform: translate(-50%, -50%) scale(1.75);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.18);
  filter: saturate(1.15);
}

.cluster-hotspot.is-active .overlay-mark::before {
  transform: translate(-50%, -50%) scale(1.85);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.18);
  filter: saturate(1.15);
}

.mark-label {
  position: relative;
  z-index: 2;
  font-size: 9px;
  line-height: 1;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.35);
  pointer-events: none;
}

.mark-caida::before {
  background: #7e57c2;
}

.mark-micro::before {
  background: #fb8c00;
}

.mark-paro::before {
  background: #e53935;
}

.mark-mix::before {
  background: #607d8b;
}

.cluster-fan {
  position: absolute;
  left: 50%;
  top: 0;
  width: 0;
  height: 72px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease;
}

.cluster-hotspot.is-active .cluster-fan {
  opacity: 1;
  pointer-events: auto;
}

.event-needle {
  position: absolute;
  left: 0;
  top: 8px;
  width: 3px;
  height: 46px;
  border: 0;
  border-radius: 999px;
  transform: translateX(var(--fan-offset, 0));
  cursor: pointer;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
  transition: transform 120ms ease, width 120ms ease, box-shadow 120ms ease;
  padding: 0;
}

.event-needle:hover,
.event-needle:focus {
  width: 6px;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.22);
}

.event-caida {
  background: #7e57c2;
}

.event-micro {
  background: #fb8c00;
}

.event-paro {
  background: #e53935;
}

.cluster-inspector {
  position: absolute;
  top: 58px;
  transform: translateX(-50%);
  z-index: 9;
  background: #fafafa;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  padding: 6px 8px;
  min-width: 220px;
  max-width: 340px;
}

.inspector-title {
  font-size: 11px;
  font-weight: 700;
  color: #37474f;
  margin-bottom: 4px;
}

.inspector-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #263238;
  line-height: 1.25;
  margin: 2px 0;
}

.inspector-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex: 0 0 8px;
}

.dot-caida {
  background: #7e57c2;
}

.dot-micro {
  background: #fb8c00;
}

.dot-paro {
  background: #e53935;
}
</style>
